/**
 * The error types the browser transport policy classifies on (COI-159).
 *
 * Shared by both browser clients — `packages/web` and
 * `packages/third-party-portal` — because they are `instanceof`-checked, and an
 * `instanceof` is only as good as the module identity behind it. Two copies of
 * these classes would each match only the errors their own app threw, so the
 * portal's copy of the retry policy would quietly stop recognising the portal's
 * own auth aborts the moment either file drifted.
 */

/**
 * The furthest this walks a `cause` chain. Every chain it is used on is one or
 * two links long (`TRPCClientError.from` wraps once and returns an existing
 * `TRPCClientError` untouched), so the bound is only there to make a cyclic
 * `cause` — which nothing constructs today, and which `instanceof` cannot
 * detect — impossible to hang on.
 */
const MAX_CAUSE_DEPTH = 5;

/**
 * The `errorClass` instance inside `error`, if any: the error itself, or
 * anything down its `cause` chain. Consumers downstream of a link see the
 * `TRPCClientError` wrapper rather than the error that was actually thrown, so
 * a bare `instanceof` on whatever the query cache handed them finds nothing.
 *
 * Returns the instance rather than a boolean so callers that need its own
 * fields (the status it carries, the original Auth0 rejection) do not have to
 * unwrap a second time.
 *
 * `errorClass` holds a constructor, which would conventionally be PascalCase,
 * but the repo's Datadog `typescript-code-style/parameter-name` rule applies to
 * every parameter — not worth a suppression for a casing preference.
 */
export const findErrorInCauseChain = <T extends Error>(
  error: unknown,
  errorClass: abstract new (...args: never[]) => T
): T | undefined => {
  let current = error;

  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
    if (current instanceof errorClass) {
      return current;
    }
    if (!(current instanceof Error)) {
      return undefined;
    }
    current = current.cause;
  }

  return undefined;
};

/**
 * An HTTP failure whose body could never have been parsed, carrying the status
 * that would otherwise have been lost (COI-159). Thrown by
 * `createStatusPreservingFetch`; read back out by `getTrpcHttpStatus`.
 */
export class TrpcHttpStatusError extends Error {
  override name = 'TrpcHttpStatusError';
  readonly status: number;

  constructor(status: number) {
    super(`tRPC request failed with HTTP ${status}`);
    this.status = status;
  }
}

/**
 * The tRPC client had no usable access token, so the `headers()` callback in
 * `createTrpcAuthHeaders` aborted the request instead of sending it
 * unauthenticated (COI-897).
 *
 * Two subclasses rather than one flag because the causes need different
 * handling AND different telemetry — before COI-897 both returned `{}` and
 * arrived at the server as the same `UnauthorizedError: No authorization token
 * was found`, which made them indistinguishable. Named after the pattern in
 * `GraphQLOperationError` (web's utils/captureGraphqlError.ts) so each gets its
 * own signature.
 *
 * Note `@trpc/client` re-wraps anything thrown from `headers()` as
 * `TRPCClientError.from(err)`, i.e. `new TRPCClientError(err.message, { cause:
 * err })` — so consumers see a `TRPCClientError` whose `name` is always
 * `TRPCClientError` and whose `cause` is one of these. It is the *message* that
 * separates the two in Sentry, which is why both are fixed strings carrying no
 * interpolated data — and why callers must unwrap rather than use a bare
 * `instanceof` on whatever the query cache handed them.
 */
export abstract class TrpcAuthAbortError extends Error {}

/**
 * Auth0 had not finished bootstrapping (`isLoading`, or not yet authenticated),
 * so the request could never have carried a token. Expected during normal
 * start-up, and recovered once auth settles by the retry policy —
 * `shouldRetryTrpcQuery` classifies it as transient and gives it the 3-attempt
 * default. Suppressed from reporting, and explicitly NOT a trigger for re-auth
 * (the login flow itself runs in this state).
 */
export class TrpcAuthNotReadyError extends TrpcAuthAbortError {
  override name = 'TrpcAuthNotReadyError';
  constructor(options?: ErrorOptions) {
    super('tRPC request aborted: auth state not ready', options);
  }
}

/**
 * `getAccessTokenSilently()` rejected — the session cannot be renewed silently
 * (rotated-out or missing refresh token, revoked session, blocked third-party
 * cookies, a machine resuming from sleep). Drives re-authentication via
 * `/logout`, matching the GraphQL path (RSP-4478).
 */
export class TrpcTokenRefreshError extends TrpcAuthAbortError {
  override name = 'TrpcTokenRefreshError';
  constructor(options?: ErrorOptions) {
    super('tRPC request aborted: Auth0 silent token refresh failed', options);
  }
}
